import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { uploadRetrievalDocument } from '../../redux/slice/documentRetrievalDocument';
import { useAuth } from "../../context/AuthContext";

const DocumentUploadContainer = styled.div`
  width: 700px;
  margin: 0rem auto;
  margin-top:5rem;
  padding: 2rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h2`
  color: #2d3748;
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  text-align:center;
`;

const UploadCard = styled.div`
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
  background-color: ${props => props.isDragging ? '#f7fafc' : '#fff'};
  border-color: ${props => props.isDragging ? '#4299e1' : '#e2e8f0'};

  &:hover {
    border-color: #4299e1;
  }
`;

const UploadInput = styled.input`
  display: none;
`;

const UploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

const UploadIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: #ebf8ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;

  svg {
    width: 30px;
    height: 30px;
    color: #3182ce;
  }
`;

const UploadText = styled.p`
  color: #4a5568;
  margin-bottom: 0.5rem;
  font-size: 1rem;
`;

const UploadHint = styled.p`
  color: #718096;
  font-size: 0.875rem;
`;

const FileName = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #f7fafc;
  border-radius: 4px;
  color: #2d3748;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #e53e3e;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.25rem;

  &:hover {
    text-decoration: underline;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3182ce;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  }
`;

const SubmitButton = styled.button`
  background-color: #3182ce;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #2c5282;
  }

  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  text-align: center;
`;

const SuccessMessage = styled(StatusMessage)`
  background-color: #f0fff4;
  color: #2f855a;
`;

const ErrorMessage = styled(StatusMessage)`
  background-color: #fff5f5;
  color: #c53030;
`;

const DocumentRetrievalUpload = () => {
  const { authState } = useAuth();
  const dispatch = useDispatch();

  const jwtBearer = authState.token;

  const [partnerLoanId, setPartnerLoanId] = useState('');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file || !partnerLoanId) {
      setMessage("Please provide both a file and a valid loan ID.");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await dispatch(
        uploadRetrievalDocument({
          jwtBearer,
          file,
          partner_loan_id: partnerLoanId,
        })
      );

      console.log("response: ", response);

      const success = response?.payload?.oxyzo_response?.success;
      const errorMessage = response?.payload?.oxyzo_response?.errorMessage;

      if (!success) {
        setMessage(errorMessage || "Document upload failed.");
        setMessageType("error");
      } else {
        setMessage("Document uploaded successfully.");
        setMessageType("success");
        setFile(null);
        setPartnerLoanId('');
      }
    } catch (error) {
      console.error("Upload error:", error);
      setMessage("An unexpected error occurred.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DocumentUploadContainer>
      <Title>Document Retrieval Upload</Title>

      <form onSubmit={handleSubmit}>
        {message && (
          messageType === 'success' ? (
            <SuccessMessage>{message}</SuccessMessage>
          ) : (
            <ErrorMessage>{message}</ErrorMessage>
          )
        )}

        <FormGroup>
          <Label htmlFor="partnerLoanId">Partner Loan ID</Label>
          <Input
            id="partnerLoanId"
            type="text"
            value={partnerLoanId}
            onChange={(e) => setPartnerLoanId(e.target.value)}
            placeholder="Enter partner loan ID"
            required
          />
        </FormGroup>

        <UploadCard
          isDragging={isDragging}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <UploadInput
            id="documentUpload"
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
          <UploadLabel htmlFor="documentUpload">
            <UploadIcon>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </UploadIcon>
            <UploadText>Drag & drop your document here or click to browse</UploadText>
            <UploadHint>Supports: PDF, DOC, DOCX, JPG, PNG (Max 10MB)</UploadHint>
          </UploadLabel>
        </UploadCard>

        {file && (
          <FileName>
            <span>{file.name}</span>
            <RemoveButton type="button" onClick={removeFile}>
              Remove
            </RemoveButton>
          </FileName>
        )}

        <SubmitButton
          type="submit"
          disabled={!file || !partnerLoanId || isLoading}
        >
          {isLoading ? (
            <>
              <LoadingSpinner />
              Uploading...
            </>
          ) : (
            'Upload Document'
          )}
        </SubmitButton>
      </form>
    </DocumentUploadContainer>
  );
};

export default DocumentRetrievalUpload;
